/**
 * deep inflate
 * @param {object} base - thing to inflate
 * @param {object} schema - thing to assign to base
 * @return {object} inflated base
 */
export const deepInflate = (base, schema) => {
  const branch = Object.assign({}, base)
  for (let p in schema) {
    const type = Object.prototype.toString.call(base[p])
    const isObj = type === '[object Object]'
    const isArr = type === '[object Array]'
    // forgot that '' coerces to false
    if (schema[p] === '' || schema[p] || schema[p] === null) {
      if (isObj) {
        // recurse
        branch[p] = deepInflate(base[p], schema[p])
      } else if ((isArr || !base[p]) && typeof schema[p] === 'function') {
        // fn can either map, or set empty array
        branch[p] = !base[p]
          ? []
          : base[p].map((item, i, arr) =>
              deepInflate(item, schema[p](item, i, arr))
            )
      } else {
        // take base if it has, else override
        branch[p] = base[p] || schema[p]
      }
    }
  }
  return branch
}

/**
 * takes story and inflates with picklist defaults, if there is no picklist default, nothing is set
 *
 * picklists object should look like this,
 * {
 *   key: {
 *     picklist: [{
 *       default: <boolean>,
 *       active: <boolean>,
 *       id: <string>
 *       text: <string>
 *       order: <number>
 *     }],
 *     orderType: <string>
 *   }
 * }
 *
 * config object should look like
 * {
 *   key: { picklist?: <string>, path?: <string>  }
 * }
 *
 * keys in config and picklists should match
 * if there is a key present in picklists but not in config,
 * it will be ignored and that property on the story object will not be tampered with
 *
 * @param {object} story
 * @param {object} picklists
 * @param {object} config
 * @return {object} ans story
 */
export const inflateStoryWithPicklistDefaults = (
  story = {},
  picklists = {},
  config = {}
) => {
  const inflated = {
    ...story,
    ...Object.keys(picklists).reduce((storyObj, picklist) => {
      // only take valid fields, and only take those that have picklist enabled
      if (!config[picklist] || !config[picklist].picklist) {
        return storyObj
      }
      // if no path specified, it is root level
      const path = (config[picklist] || {}).path
      const arr = (path || picklist).split('.')
      const rootKey = arr[0]
      // do this after valid field check, prevents malformed data from passing through
      const picklistDefault = (
        (picklists[picklist] || { picklist: [] }).picklist.find(
          (p) => p.default === true
        ) || {}
      ).id
      // this prevents us from setting a prop as undefined on story obj
      if (arr.length < 1 && !picklistDefault) {
        return storyObj
      }
      // rootKey === 'test' && console.log({rootKey, storyObj, arr, prop: storyObj[rootKey]})
      // arr.length === 1 && console.log({storyObj, rootKey, picklist, arr, path})
      return {
        ...storyObj,
        // use root key if path defaults to root level
        [rootKey]:
          arr.length === 1
            ? storyObj[rootKey] || picklistDefault
            : // ? storyObj[rootKey] ? storyObj[rootKey] : picklistDefault
              // fold path to deep inflate, leaf is each key (split form arr) in meta config "path"
              arr.slice(1).reduce(
                ({ tree, branch = tree }, leaf, i, { length }) => {
                  branch[leaf] =
                    i !== length - 1
                      ? branch[leaf] || {}
                      : // on deepest leaf take what the user has saved
                      // if they have not saved anything, take picklist default
                      branch[leaf]
                      ? branch[leaf]
                      : picklistDefault
                  return { tree, branch: branch[leaf] }
                  // start with what we already have, or inflate with emtpy obj
                },
                { tree: storyObj[rootKey] || {} }
              ).tree,
      }
    }, story),
  }
  return inflated
}

export const removePicklistOverrides = (story, config = []) => {
  config.forEach((obj) => {
    if (obj.reset === true) {
      obj.path.split('.').reduce((acc, branch, i, { length }) => {
        if (i === length - 1) {
          // this makes the ui behave properly
          // but technically downstream, ANS will not remove empty strings
          acc[branch] = ''
          // this screws up angular ui,
          // makes the dropdown contain double blank options
          // delete acc[branch]
        }
        return acc[branch]
      }, story)
    }
  })
  return story
}
